import { Typography } from '@mui/material';
import Stack from '@mui/material/Stack';
import { TScanQualityCheck } from './types';
import tokens from '@verifime/design-tokens';
import ErrorIcon from '@mui/icons-material/Error';

export type TShowScanQualityIssuesProps = {
  failedChecks: TScanQualityCheck[];
};

/**
 * Maps each scan quality check type (`TScanQualityCheck`) to a user-friendly error message.
 *
 * This record provides descriptive messages for various scan quality issues that may be detected,
 * such as glare, focus, resolution, color contrast, perspective, bounds, portrait extraction,
 * brightness, and occlusion. These messages can be displayed to users to help them understand
 * and resolve issues with their scanned documents.
 *
 * `Other` is the backend's catch-all for an unrecognised check, so its message stays generic:
 * we know a quality check failed but not which one.
 */
const messages: Record<TScanQualityCheck, string> = {
  ImageGlares: 'Image has glare that may affect readability.',
  ImageFocus: 'Image is not in focus.',
  ImageResolution: 'Image resolution is too low.',
  ImageColorness: 'Image lacks sufficient color contrast.',
  Perspective: 'Image perspective is skewed.',
  Bounds: 'The image may not contain the entire document or has excessive margins.',
  Portrait: 'Document portrait may not have been extracted correctly.',
  Brightness: 'Image brightness is too low or too high.',
  Occlusion: 'Document is covered by other objects or has excessive shadows.',
  Other: 'An image quality issue was detected. Please retake the photo and try again.',
};

/**
 * Displays a summary of detected image quality issues based on failed scan checks.
 *
 * @param failedChecks - An array of failed check identifiers indicating which image quality checks did not pass.
 *
 * If no issues are detected (`failedChecks` is empty), a message indicating no issues is shown.
 * Otherwise, a list of detected issues is displayed, each with an error icon and a descriptive message.
 *
 * @remarks
 * - Uses Material UI components for layout and styling.
 * - Expects a `messages` object mapping check identifiers to user-friendly descriptions.
 */
export default function ShowScanQualityIssues({
  failedChecks,
}: Readonly<TShowScanQualityIssuesProps>) {
  return (
    <>
      <Typography variant="h6" component="h6">
        Detected Image Quality Issues
      </Typography>
      {failedChecks.length === 0 ? (
        <Typography variant="caption" sx={{ mt: tokens.spacingXs }}>
          No issues detected.
        </Typography>
      ) : (
        <>
          <Typography variant="caption" sx={{ mt: tokens.spacingXs }}>
            The following issues were detected with the scan:
          </Typography>
          <Stack spacing={tokens.spacing3xs} sx={{ mt: tokens.spacingXs }}>
            {failedChecks.map((check) => (
              <Stack key={check} direction="row" spacing={tokens.spacingXs} alignItems="center">
                <ErrorIcon sx={{ fontSize: 'small' }} />
                <Typography variant="caption">{messages[check]}</Typography>
              </Stack>
            ))}
          </Stack>
        </>
      )}
    </>
  );
}
